import { useIsSmOrSmaller } from '@dreamstack/feature-components/src/hooks/useWindowSize'
import {
  HeadingH3,
  Text_13_18,
  Text_16_24,
  Text_20_30,
  Text_24_36,
} from '@dreamstack/feature-components/src/layout/Texts'
import { useFormatToCurrencyCutoff, useTranslation } from '@dreamstack/i18n'
import { ArrowRightLong } from '@dreamstack/icons/src/icon-components/ArrowRightLong'
import { clsxMerge } from '@dreamstack/util'
import { ResponsiveWrapper } from '@nivo/core'
import axios from 'axios'
import { geoMercator, geoPath } from 'd3-geo'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useGraphFontsizeCb } from '../HomeOwnershipReport/useGraphFontsize'
import { auswanderung_data } from './data/auswanderung_data'
import type { ScaleAndProjection } from './libs/helperFunctions'
import { getScaleAndProjection } from './libs/helperFunctions'
import type {
  ArrowIndicationProps,
  FeatureData,
  GeometryData,
  RegionGeoMapProps,
  RegionGeoMapValueData,
} from './libs/typings'
import { useDataForAuswanderung } from './libs/useDataForAuswanderung'

const Choropleth = dynamic(
  () => import('@nivo/geo').then((mod) => mod.Choropleth),
  {
    ssr: false,
  }
)

type ResponsiveRegionGeoMapProps = {
  data: RegionGeoMapValueData
  response: any
  features: [GeometryData] | []
  region: string
  height: number
  width: number
  scale: number
  translate: [number, number]
  saldo: number
}

export const RegionGeoMap: FunctionComponent<
  React.PropsWithChildren<RegionGeoMapProps>
> = ({ customProps }) => {
  const [response, setData] = useState<FeatureData>()
  const [data, setDataArray] = useState<[GeometryData] | []>([])
  const [bounds, setBounds] = useState({ width: 600, height: 400 })
  const region = customProps.region
  const t = useTranslation()
  const coloredMapField = { id: region.toLowerCase(), value: 1 }

  const {
    groupedForPopulation: parsedData,
    zuzuege_2023,
    fortzuege_2023,
    saldo,
    relativeChangeFortzüge,
    relativeChangeZuzüge,
  } = useDataForAuswanderung(region, auswanderung_data)

  useEffect(() => {
    async function fetchData() {
      const response = await axios.get('/api/region/fetchBerlinMapData')
      const mergedJson: [GeometryData] = response.data.data.features.map(
        (district: any) => {
          return {
            ...district,
            id: district.properties.name.toString().toLowerCase(),
          }
        }
      )
      setData(response.data)
      setDataArray(mergedJson)
    }
    fetchData()
  }, [])

  const isMobile = useIsSmOrSmaller()

  useEffect(() => {
    if (isMobile) {
      setBounds({ width: 300, height: 250 })
    } else {
      setBounds({ width: 600, height: 400 })
    }
  }, [isMobile])
  // Create a path generator.

  const { scale, translate } = useMemo(() => {
    const test = geoMercator().scale(1).translate([0, 0])
    const pat = geoPath().projection(test)

    // Compute the bounds of a feature of interest, then derive scale & translate.
    const b = pat.bounds(response?.data as any)
    const scale =
      0.95 /
      Math.max(
        (b[1][0] - b[0][0]) / bounds.width,
        (b[1][1] - b[0][1]) / bounds.height
      )

    const translate: [number, number] = [
      (bounds.width - scale * (b[1][0] + b[0][0])) / 2 / bounds.width,
      (bounds.height - scale * (b[1][1] + b[0][1])) / 2 / bounds.height,
    ]
    return { scale, translate }
  }, [bounds.height, bounds.width, response?.data])

  if (region) {
    return (
      <div className="bg-accentroAqua-100 grid grid-cols-2 p-px-24">
        {isMobile && (
          <Text_20_30 className="w-full font-semibold ">
            {t('accentro:regionGeoMap:graphHeading') + ' ' + region}
          </Text_20_30>
        )}

        <div className="md:h-[480px] h-[350px] col-span-2 md:col-span-1 md:pb-12">
          <ResponsiveWrapper>
            {({ width, height }) => {
              return (
                <ResponsiveRegionGeoMap
                  data={coloredMapField}
                  response={response}
                  features={data}
                  region={region}
                  height={height}
                  width={width}
                  scale={scale}
                  translate={translate}
                  saldo={saldo}
                />
              )
            }}
          </ResponsiveWrapper>
        </div>
        <div className="flex flex-col flex-nowrap mt-px-48 space-y-px-32 col-span-2 md:col-span-1">
          {!isMobile && (
            <HeadingH3 className="mb-px-16 w-full ">
              {t('accentro:regionGeoMap:graphHeading') + ' ' + region}
            </HeadingH3>
          )}

          <ArrowIndicationForMap
            value={zuzuege_2023}
            relativeChange={relativeChangeZuzüge}
            isIncoming={true}
          />

          <ArrowIndicationForMap
            value={fortzuege_2023}
            relativeChange={relativeChangeFortzüge}
            isIncoming={false}
          />
          <Text_13_18>
            {t(`accentro:regionGeoMap:underlineText`, { year: '2021' })}
          </Text_13_18>
        </div>
      </div>
    )
  } else
    return (
      <>
        Es muss eine Region in der Static Page gewählt werden um diese Inhalte
        anzuzeigen.
      </>
    )
}

const ArrowIndicationForMap: FunctionComponent<
  React.PropsWithChildren<ArrowIndicationProps>
> = ({ value, relativeChange, isIncoming }) => {
  const formatToDecimal = useFormatToCurrencyCutoff()
  const t = useTranslation()
  return (
    <>
      <div className="flex flex-row items-center space-x-px-24">
        <div className="relative w-px-64 h-px-64">
          {isIncoming ? (
            <>
              <ArrowRightLong className="w-px-40 h-px-20 absolute z-10 top-[20px] " />
              <div className="w-px-40 h-px-40 rounded-full absolute top-[10px] left-[16px] bg-accentroAquaLight-full"></div>
            </>
          ) : (
            <>
              <ArrowRightLong className="w-px-40 h-px-20 absolute z-10 left-[20px] top-[20px] " />
              <div className="w-px-40 h-px-40 rounded-full absolute top-[10px]  bg-accentroAquaLight-full"></div>
            </>
          )}
        </div>
        <div className="flex flex-col items-start">
          <Text_16_24 className="font-semibold">
            {isIncoming ? (
              <>{t('accentro:regionGeoMap.zuzuege')}</>
            ) : (
              <>{t('accentro:regionGeoMap.fortzuege')}</>
            )}
          </Text_16_24>

          <div className="flex flex-col items-baseline ">
            <div className="flex flex-row items-baseline flex-nowrap space-x-px-8">
              <div className="font-semibold text-[24px] md:text-[28px]">
                {formatToDecimal(value)}
              </div>
              <Text_24_36 className="font-semibold">
                {t('accentro:regionGeoMap.persons')}
              </Text_24_36>
            </div>
            <Text_16_24
              className={clsxMerge(
                'ml-px-4 font-semibold',
                relativeChange > 0
                  ? 'text-accentroLightGreen-full'
                  : 'text-accentroError-full'
              )}
            >
              {(relativeChange > 0 ? '+' : '') +
                relativeChange.toFixed(2) +
                ' %'}
            </Text_16_24>
          </div>
        </div>
      </div>
    </>
  )
}

const ResponsiveRegionGeoMap: FunctionComponent<
  React.PropsWithChildren<ResponsiveRegionGeoMapProps>
> = ({
  data,
  features,
  response,
  region,
  height,
  width,
  scale,
  translate,
  saldo,
}) => {
  const graphFontSize = useGraphFontsizeCb()
  const t = useTranslation()
  const scaleAndProjection: ScaleAndProjection | null = useMemo(() => {
    if (!features) return null
    return getScaleAndProjection(response?.data, width, height)
  }, [features, response, width, height])

  return (
    <>
      <div className="flex flex-col justify-center items-center flex-wrap">
        <div className="mt-px-32 flex flex-row justify-center ">
          <Choropleth
            theme={{
              text: {
                fontSize: graphFontSize(),
                fontFamily: 'Montserrat',
              },
            }}
            height={height}
            width={width}
            data={[data]}
            domain={[0, 2000]}
            features={features || []}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            colors={['#508090']}
            label={'properties.kr_name'}
            unknownColor="#fff"
            isInteractive={true}
            projectionScale={scaleAndProjection?.scale}
            projectionTranslation={scaleAndProjection?.translate}
            borderWidth={3}
            borderColor="#EFF3F5"
            tooltip={(data) => {
              const tooltip =
                data.feature.value > 0 ? (
                  <div
                    style={{
                      background: 'white',
                      padding: '6px 8px',
                      border: '1px solid rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '2px',
                    }}
                  >
                    <div className="flex flex-col">
                      <div className="font-semibold">{region}</div>
                      <div className="flex flex-row space-x-px-8">
                        <div className="font-semibold">
                          {t('accentro:regionGeoMap.tooltip.difference')}{' '}
                        </div>{' '}
                        <Text_16_24
                          className={clsxMerge(
                            'ml-px-4 font-semibold',
                            saldo > 0
                              ? 'text-accentroLightGreen-full'
                              : 'text-accentroError-full'
                          )}
                        >
                          {saldo}
                        </Text_16_24>
                      </div>
                    </div>
                  </div>
                ) : null

              return tooltip
            }}
          />
        </div>
      </div>
    </>
  )
}
